import {
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  Clock,
  List,
  Plus,
  Search,
  Tag,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../auth/AuthProvider"
import AddTaskModal from "../components/AddTaskModal"
import CalendarView from "../components/CalendarView"
import TaskDetailModal from "../components/TaskDetailModal"
import Card from "../components/Card"
import { cx } from "../lib/cx"
import type { Task } from "../types"

type ApiTask = {
  id: string
  title: string
  description?: string
  status: string
  assignee?: string
  assigneeId?: string
  projectId?: string
  project?: string
  clientId?: string
  client?: string
  dueDate?: string
  priority?: Task["priority"]
  estimatedHours?: number
  tags?: string[]
  createdAt?: string
  updatedAt?: string
}

const statusFilters: Array<{ value: Task["status"] | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
]

const priorityFilters: Array<{ value: Task["priority"] | "all"; label: string }> = [
  { value: "all", label: "All Priorities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
]

const TasksPage = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const taskIdFromParams = searchParams.get("taskId")
  const [tasks, setTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(searchParams.get("task") || "")
  const [statusFilter, setStatusFilter] = useState<Task["status"] | "all">("all")
  const [priorityFilter, setPriorityFilter] = useState<Task["priority"] | "all">("all")
  const [viewMode, setViewMode] = useState<"my" | "all">(searchParams.get("task") || taskIdFromParams ? "all" : "my")
  const [viewType, setViewType] = useState<"list" | "calendar">("list")

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch("/api/tasks")
        if (res.ok) {
          const data = await res.json()
          setTasks((data.tasks || []).map((t: ApiTask): Task => {
            const normalized = t.status === "Pending" ? "todo" : t.status
            const safeStatus: Task["status"] =
              normalized === "todo" || normalized === "in_progress" || normalized === "review" || normalized === "done"
                ? normalized
                : "todo"
            return {
              id: t.id,
              title: t.title,
              description: t.description || "",
              status: safeStatus,
              assignee: t.assignee || "",
              assigneeId: t.assigneeId || "",
              projectId: t.projectId || "",
              project: t.project || "",
              clientId: t.clientId || "",
              client: t.client || "",
              dueDate: t.dueDate,
              priority: t.priority || "medium",
              estimatedHours: t.estimatedHours || 0,
              tags: t.tags || [],
              createdAt: t.createdAt || new Date().toISOString(),
              updatedAt: t.updatedAt || new Date().toISOString(),
            }
          }))
        } else {
          setTasks([])
        }
      } catch {
        setTasks([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchTasks()
  }, [])

  useEffect(() => {
    if (!taskIdFromParams || tasks.length === 0) return
    const matched = tasks.find((task) => task.id === taskIdFromParams)
    if (matched) {
      setSelectedTask(matched)
      setIsModalOpen(true)
      setViewMode("all")
    }
  }, [taskIdFromParams, tasks])

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks

    // Filter by view mode
    if (viewMode === "my") {
      filtered = filtered.filter((task) => task.assigneeId === user?.id)
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          task.project?.toLowerCase().includes(query) ||
          task.client?.toLowerCase().includes(query) ||
          task.tags.some((tag) => tag.toLowerCase().includes(query)),
      )
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((task) => task.status === statusFilter)
    }

    // Filter by priority
    if (priorityFilter !== "all") {
      filtered = filtered.filter((task) => task.priority === priorityFilter)
    }

    // Sort by priority (high first) then due date
    return filtered.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 }
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority]
      if (priorityDiff !== 0) return priorityDiff

      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      }
      if (a.dueDate) return -1
      if (b.dueDate) return 1
      return 0
    })
  }, [tasks, searchQuery, statusFilter, priorityFilter, viewMode, user?.id])

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task)
    setIsModalOpen(true)
  }

  const handleTaskUpdate = async (taskId: string, updates: Partial<Task>) => {
    // Save previous state for rollback
    const prevTasks = tasks
    const prevSelected = selectedTask

    // Optimistic update
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? {
              ...task,
              ...updates,
              updatedAt: new Date().toISOString(),
              completedAt: updates.status === "done" ? new Date().toISOString() : task.completedAt,
            }
          : task,
      ),
    )
    if (selectedTask?.id === taskId) {
      setSelectedTask({ ...selectedTask, ...updates })
    }

    // Persist to API
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      })
      if (!res.ok) throw new Error("Failed")
    } catch {
      // Rollback on failure
      setTasks(prevTasks)
      setSelectedTask(prevSelected)
    }
  }

  const handleTaskCreate = async (taskData: Omit<Task, "id" | "createdAt" | "updatedAt">) => {
    const tempId = `task-${Date.now()}`
    const newTask: Task = {
      ...taskData,
      id: tempId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    // Optimistic add
    setTasks((prev) => [...prev, newTask])
    setIsAddModalOpen(false)

    // Persist to API
    try {
      const res = await fetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(taskData),
      })
      if (res.ok) {
        const data = await res.json()
        // Replace temp ID with real ID from backend
        setTasks((prev) =>
          prev.map((t) => (t.id === tempId ? { ...t, id: data.id } : t)),
        )
      } else {
        throw new Error("Failed to create task")
      }
    } catch {
      // Roll back optimistic row so the list reflects persisted state.
      setTasks((prev) => prev.filter((t) => t.id !== tempId))
    }
  }

  const handleTaskDelete = async (taskId: string) => {
    const prevTasks = tasks

    setTasks((prev) => prev.filter((task) => task.id !== taskId))
    if (selectedTask?.id === taskId) {
      setIsModalOpen(false)
      setSelectedTask(null)
    }

    // Persist to API
    try {
      const res = await fetch(`/api/tasks/${taskId}`, { method: "DELETE" })
      if (!res.ok) throw new Error("Failed")
    } catch {
      // Rollback on failure
      setTasks(prevTasks)
    }
  }

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return "No due date"
    const date = new Date(dueDate)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return "Overdue"
    if (diffDays === 0) return "Today"
    if (diffDays === 1) return "Tomorrow"
    if (diffDays < 7) return `In ${diffDays} days`
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }

  const getStatusColor = (status: Task["status"]) => {
    const colors = {
      todo: "bg-slate-100 text-slate-700",
      in_progress: "bg-blue-100 text-blue-700",
      review: "bg-yellow-100 text-yellow-700",
      done: "bg-green-100 text-green-700",
    }
    return colors[status]
  }

  const getPriorityColor = (priority: Task["priority"]) => {
    const colors = {
      low: "bg-slate-100 text-slate-700",
      medium: "bg-yellow-100 text-yellow-700",
      high: "bg-rose-100 text-rose-700",
    }
    return colors[priority]
  }

  const stats = useMemo(() => {
    const myTasks = tasks.filter((t) => t.assigneeId === user?.id)
    return {
      total: myTasks.length,
      todo: myTasks.filter((t) => t.status === "todo").length,
      inProgress: myTasks.filter((t) => t.status === "in_progress").length,
      review: myTasks.filter((t) => t.status === "review").length,
      done: myTasks.filter((t) => t.status === "done").length,
    }
  }, [tasks, user?.id])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-stone">Loading tasks...</div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Tasks</h1>
              <p className="mt-1 text-sm text-stone">Manage your tasks and track progress</p>
            </div>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90"
            >
              <Plus className="h-4 w-4" />
              Add Task
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode("my")}
              className={cx(
                "rounded-lg px-4 py-2 text-sm font-medium transition",
                viewMode === "my"
                  ? "bg-moss text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200",
              )}
            >
              My Tasks ({stats.total})
            </button>
            <button
              type="button"
              onClick={() => setViewMode("all")}
              className={cx(
                "rounded-lg px-4 py-2 text-sm font-medium transition",
                viewMode === "all"
                  ? "bg-moss text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200",
              )}
            >
              All Tasks ({tasks.length})
            </button>

            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewType("list")}
                className={cx(
                  "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition",
                  viewType === "list"
                    ? "bg-moss text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                )}
              >
                <List className="h-4 w-4" />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewType("calendar")}
                className={cx(
                  "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition",
                  viewType === "calendar"
                    ? "bg-moss text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                Calendar
              </button>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Card>
            <div className="text-center">
              <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
              <p className="text-xs text-stone">Total</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-semibold text-slate-900">{stats.todo}</p>
              <p className="text-xs text-stone">To Do</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-semibold text-blue-700">{stats.inProgress}</p>
              <p className="text-xs text-stone">In Progress</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-semibold text-yellow-700">{stats.review}</p>
              <p className="text-xs text-stone">Review</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-semibold text-green-700">{stats.done}</p>
              <p className="text-xs text-stone">Done</p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className="w-full rounded-full border border-line bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-moss"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as Task["status"] | "all")
                }
                className="appearance-none rounded-lg border border-line bg-white px-4 py-2 pr-8 text-sm outline-none focus:border-moss"
              >
                {statusFilters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-stone" />
            </div>

            {/* Priority Filter */}
            <div className="relative">
              <select
                value={priorityFilter}
                onChange={(e) =>
                  setPriorityFilter(e.target.value as Task["priority"] | "all")
                }
                className="appearance-none rounded-lg border border-line bg-white px-4 py-2 pr-8 text-sm outline-none focus:border-moss"
              >
                {priorityFilters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-stone" />
            </div>
          </div>
        </Card>

        {/* Task List / Calendar */}
        {viewType === "calendar" ? (
          <CalendarView tasks={filteredTasks} onTaskClick={handleTaskClick} />
        ) : (
        <div className="space-y-3">
          {filteredTasks.length > 0 ? (
            filteredTasks.map((task) => (
              <Card
                key={task.id}
                className="cursor-pointer transition hover:shadow-md"
                onClick={() => handleTaskClick(task)}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleTaskUpdate(task.id, {
                        status: task.status === "done" ? "todo" : "done",
                      })
                    }}
                    className={cx(
                      "mt-0.5 flex h-5 w-5 items-center justify-center rounded border-2 transition",
                      task.status === "done"
                        ? "border-moss bg-moss text-white"
                        : "border-line hover:border-moss",
                    )}
                  >
                    {task.status === "done" && <Check className="h-3 w-3" />}
                  </button>

                  {/* Task Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3
                          className={cx(
                            "font-semibold",
                            task.status === "done" ? "text-stone line-through" : "text-slate-900",
                          )}
                        >
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="mt-1 text-sm text-stone line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-stone">
                          {task.project && (
                            <span className="flex items-center gap-1">
                              <Tag className="h-3 w-3" />
                              {task.project}
                            </span>
                          )}
                          {task.dueDate && (
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              {formatDueDate(task.dueDate)}
                            </span>
                          )}
                          {(task.estimatedHours || task.actualHours) && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {task.actualHours || 0}/{task.estimatedHours || 0}h
                            </span>
                          )}
                        </div>
                        {task.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {task.tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Status and Priority */}
                      <div className="flex flex-col items-end gap-2">
                        <span
                          className={cx(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            getStatusColor(task.status),
                          )}
                        >
                          {statusFilters.find((f) => f.value === task.status)?.label}
                        </span>
                        <span
                          className={cx(
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            getPriorityColor(task.priority),
                          )}
                        >
                          {task.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card>
              <div className="py-12 text-center">
                <p className="text-sm text-stone">No tasks found</p>
                <p className="mt-1 text-xs text-stone">Try adjusting your filters</p>
              </div>
            </Card>
          )}
        </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedTask(null)
        }}
        onUpdate={handleTaskUpdate}
        onDelete={handleTaskDelete}
      />

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleTaskCreate}
      />
    </>
  )
}

export default TasksPage
